import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { UnverifiedUserEmailAlertModalConfig } from '@front-platform/content-builder';
import {
  resolveCloseIconFieldColors,
  unverifiedUserEmailAlertModalResolveConfig,
} from '@front-platform/content-builder';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { exhaustMap, map, Subject } from 'rxjs';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ContentBuilderColorPipe } from '../../../core/pipes/content-builder-color.pipe';
import { AuthVerificationEmailService } from '@core/services/auth/auth-verification-email.service';
import { emptyStatedNotLoadedData, statedStream } from '@shared/utils/rxjs/stated-steam';

export type UnverifiedUserEmailAlertModalData = ModalConfig<{
  handle: typeof UnverifiedUserEmailAlertModalComponent.handle;
  inputs: {
    headingColor?: IContentBuilderFieldColor;
    closeIconFieldColors?: ContentBuilderFieldColors;
  };
}>;
export interface UnverifiedUserEmailAlertModalRuntimeConfig {}

export type UnverifiedUserEmailAlertModalConfigInput = Partial<
  Omit<UnverifiedUserEmailAlertModalConfig, keyof UnverifiedUserEmailAlertModalRuntimeConfig> &
    UnverifiedUserEmailAlertModalRuntimeConfig
>;
export type UnverifiedUserEmailAlertModalResolvedConfig = Omit<
  UnverifiedUserEmailAlertModalConfig,
  keyof UnverifiedUserEmailAlertModalRuntimeConfig
> &
  Partial<UnverifiedUserEmailAlertModalRuntimeConfig>;
export interface UnverifiedUserEmailAlertModalContract extends ConfigComponentContract<
  UnverifiedUserEmailAlertModalConfigInput,
  UnverifiedUserEmailAlertModalResolvedConfig
> {}

@Component({
  selector: 'app-unverified-user-email-alert-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalFillerAtomComponent,
    ButtonAtomComponent,
    TranslateModule,
    ContentBuilderColorPipe,
  ],
  template: `
    <app-modal-filler-atom
      (onClick)="close()"
      [index]="index()"
    ></app-modal-filler-atom>
    <div
      class="fixed left-0 right-0 overflow-y-auto shadow md:left-0 md:top-0 md:flex md:h-full md:w-full md:items-center md:justify-center mdMax:bottom-0 mdMax:mx-auto mdMax:max-h-85-screen mdMax:max-w-screen-sm"
      [style.z-index]="index() + 50"
    >
      @if (isDesktop()) {
        <app-modal-filler-atom
          [withBackdrop]="false"
          (onClick)="close()"
          [index]="index()"
        ></app-modal-filler-atom>
      }
      <div
        class="relative flex flex-col rounded-t-3xl bg-white p-5 md:max-w-lg md:rounded-3xl md:p-7"
        [style.z-index]="index() + 50"
      >
        <div class="space-y-5 px-4.5 py-5">
          <div class="flex items-center">
            <div
              class="flex flex-1 items-center"
              [style.color]="headingColor() | contentBuilderColor: '#000'"
            >
              <i class="icon icon-email mr-3"></i>
              <span class="font-base font-bold lg:text-md">{{
                'unverified-user-email-alert-modal.title' | translate
              }}</span>
            </div>
            <button
              (click)="close()"
              class="icon icon-cross h-8 w-8 rounded-full text-xs"
              [style.backgroundColor]="
                closeIconFieldColors()?.background
                  | contentBuilderColor: 'var(--ui-color-primary-200)'
              "
              [style.color]="
                closeIconFieldColors()?.font | contentBuilderColor: 'var(--ui-color-primary-900)'
              "
            ></button>
          </div>

          <div class="flex items-center justify-between">
            <p>
              {{
                'unverified-user-email-alert-modal.description'
                  | translate
                    : {
                        email: userEmail(),
                      }
              }}
            </p>
          </div>

          <div class="flex items-center justify-between">
            <p class="text-sm">
              {{ 'unverified-user-email-alert-modal.checkSpam' | translate }}
            </p>
          </div>

          <div class="flex items-center justify-center gap-5">
            <app-button-atom
              class="block flex-1"
              [label]="buttonLabel()"
              [type]="'primary'"
              [color]="'primary'"
              [full]="true"
              [disabled]="!canSendVerificationEmail()"
              (clickEventEmitter)="sendVerificationEmailAgain()"
              [iconUrl]="
                sendingVerificationEmailAgain().isLoading ? 'assets/images/spinner.svg' : ''
              "
            />
          </div>
          @if (sendingVerificationEmailAgain().isLoaded) {
            <div class="flex items-center justify-between">
              <p class="text-sm text-success-500">
                {{ 'unverified-user-email-alert-modal.success' | translate }}
              </p>
            </div>
          }
          @if (sendingVerificationEmailAgain().hasError) {
            <div class="flex items-center justify-between">
              <p class="text-sm text-error-500">
                {{ 'unverified-user-email-alert-modal.error' | translate }}
              </p>
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class UnverifiedUserEmailAlertModalComponent
  extends ModalScrollBlockBase
  implements
    GetModalInterface<UnverifiedUserEmailAlertModalData>,
    UnverifiedUserEmailAlertModalContract
{
  readonly config = input<UnverifiedUserEmailAlertModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    UnverifiedUserEmailAlertModalConfig,
    UnverifiedUserEmailAlertModalRuntimeConfig
  >(unverifiedUserEmailAlertModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'unverified-user-email-alert-modal' as const;

  private readonly modalsService: ModalsService<UnverifiedUserEmailAlertModalData> =
    inject(ModalsService);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly authVerificationEmailService = inject(AuthVerificationEmailService);
  private readonly translate = inject(TranslateService);

  public readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  public readonly closeIconFieldColors = computed(() => {
    const cfg = this.resolvedConfig() as Record<string, unknown>;
    return resolveCloseIconFieldColors(cfg, this.config() as Record<string, unknown>);
  });

  protected readonly userEmail = computed(() => this.authStoreSelector.selectUser()?.email ?? '');
  protected readonly canSendVerificationEmail =
    this.authVerificationEmailService.canSendVerificationEmail;
  protected readonly remainingTimeBeforeSendingAVerificationEmail =
    this.authVerificationEmailService.remainingTimeBeforeSendingAVerificationEmail;

  protected readonly timeRemaining = computed(() => {
    const remainingTime = this.remainingTimeBeforeSendingAVerificationEmail();
    if (!remainingTime) return null;

    const now = new Date();
    const timeLeft = (remainingTime as Date).getTime() - now.getTime();
    const minutesLeft = Math.ceil(timeLeft / (1000 * 60));
    const secondsLeft = Math.ceil((timeLeft % (1000 * 60)) / 1000);

    if (minutesLeft > 0) {
      return { minutes: minutesLeft, seconds: 0 };
    } else {
      return { minutes: 0, seconds: Math.max(0, secondsLeft) };
    }
  });

  protected readonly buttonLabel = computed(() => {
    const canSend = this.canSendVerificationEmail();
    const timeLeft = this.timeRemaining();

    if (canSend || !timeLeft) {
      return this.translate.instant('unverified-user-email-alert-modal.sendAgain');
    }

    if (timeLeft.minutes > 0) {
      return this.translate.instant('unverified-user-email-alert-modal.waitMinutes', {
        minutes: timeLeft.minutes,
      });
    } else {
      return this.translate.instant('unverified-user-email-alert-modal.waitSeconds', {
        seconds: timeLeft.seconds,
      });
    }
  });

  protected readonly index = toSignal(
    this.modalsService
      .getData('unverified-user-email-alert-modal')
      .pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );

  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);

  protected close(): void {
    this.modalsService.close('unverified-user-email-alert-modal');
  }

  protected sendVerificationEmailAgain() {
    this.sendVerificationEmailAgain$.next();
  }

  private readonly sendVerificationEmailAgain$ = new Subject<void>();

  protected readonly sendingVerificationEmailAgain = toSignal(
    this.sendVerificationEmailAgain$.pipe(
      exhaustMap(() => statedStream(this.authVerificationEmailService.sendVerificationEmail$())),
    ),
    {
      initialValue: emptyStatedNotLoadedData<null>(),
    },
  );

  constructor() {
    super();
    // Auto send verification email when opening the modal if possible (not already sent recently)
    this.sendVerificationEmailAgain$.next();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(UnverifiedUserEmailAlertModalComponent, {
  'keyAliases': {},
});
